import { useState } from 'react'
import { usePathname } from 'next/navigation'

import { useActionless, useAppSelector } from 'redux/hooks'
import { SET_COLLECTION_V2_STATE } from 'redux/collectionV2/actions/types'

import Paginator, { ButtonPaginator } from 'components/elements/Paginator'
import PhatDropdown from 'components/formElements/PhatDropdown'
import PhatInput from 'components/formElements/PhatInput'

import { setCollectionQueryParams } from 'utils/queryParams/collection.queryParams'

import styles from './paginationControls.module.scss'

type Props = {
  page: number
  totalPages: number
  hideRowsPerPage?: boolean
  className?: string
}

const PaginationControls = ({ className = '', page, totalPages, hideRowsPerPage }: Props) => {
  const pathname = usePathname()
  const preferredPageSize = useAppSelector(state => state.collectionV2.preferredPageSize)

  const [setCollectionV2State] = useActionless(SET_COLLECTION_V2_STATE)

  const queryString = typeof window === 'undefined' ? '' : window?.location?.search || ''
  const urlParams = new URLSearchParams(queryString)

  const [gotoPage, setGotoPage] = useState<number | string>()

  urlParams.delete('page')

  const url = `${pathname}?${urlParams.toString()}`

  const handleUpdatePageSize = (pageSize: number) => {
    setCollectionV2State({ preferredPageSize: pageSize })
    setCollectionQueryParams({ pageSize, page: 1 })
  }

  return (
    <div className={className}>
      <div className={styles.paginationControls}>
        {!hideRowsPerPage && (
          <PhatDropdown
            className={styles.pageSizeDropdown}
            value={preferredPageSize}
            header="Rows per page"
            options={[
              { label: '25', id: '25', onClick: () => handleUpdatePageSize(25) },
              { label: '50', id: '50', onClick: () => handleUpdatePageSize(50) },
              { label: '100', id: '100', onClick: () => handleUpdatePageSize(100) },
              { label: '250', id: '250', onClick: () => handleUpdatePageSize(250) },
            ]}
          />
        )}

        <Paginator activePage={page} totalPages={totalPages} urlBeforePageNumber={`${url}&page=`} />

        <form
          className={styles.exactPage}
          onSubmit={e => {
            e.preventDefault()

            setCollectionQueryParams({ page: gotoPage })
          }}>
          <PhatInput
            className={styles.exactPage}
            header="Exact Page"
            placeholder={`${page}`}
            value={gotoPage}
            type="number"
            onChange={setGotoPage}
          />
        </form>
      </div>
    </div>
  )
}

export default PaginationControls

type ButtonPaginationProps = {
  activePage: number
  totalPages: number
  onPageChange: (pageNumber: number) => void
  className?: string
}

export const ButtonPaginationControls = ({
  className = '',
  activePage,
  totalPages,
  onPageChange,
}: ButtonPaginationProps) => {
  const [gotoPage, setGotoPage] = useState<number | string>()

  return (
    <div className={`${styles.paginationControls} ${className}`}>
      <ButtonPaginator activePage={activePage} totalPages={totalPages} onPageChange={onPageChange} />

      <form
        className={styles.exactPage}
        onSubmit={e => {
          e.preventDefault()

          onPageChange(Number(gotoPage))
        }}>
        <PhatInput
          className={styles.exactPage}
          header="Exact Page"
          placeholder={`${activePage}`}
          value={gotoPage}
          type="number"
          onChange={setGotoPage}
        />
      </form>
    </div>
  )
}
